import React from 'react';
import { Box, Typography, Button } from '@mui/material';
import { motion } from 'framer-motion';

type NoPermissionMessageType = {
    height?: string;
};

const containerVariants = {
    initial: { opacity: 0, y: 30 },
    animate: {
        opacity: 1,
        y: 0,
        transition: {
            duration: 0.8,
            ease: 'easeOut',
            when: 'beforeChildren',
            staggerChildren: 0.2,
        },
    },
};

const fadeInUp = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
};

const NoPermissionMessage: React.FC<NoPermissionMessageType> = ({ height = '100vh' }) => {
    return (
        <Box
            component={motion.div}
            // variants={containerVariants}
            initial="initial"
            animate="animate"
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: height,
                textAlign: 'center',
                p: { xs: 4, md: 8 },
                margin: '0 auto',
                position: 'relative',
                overflow: 'hidden',
            }}
        >
            <Box
                sx={{
                    position: 'absolute',
                    top: '-10%',
                    right: '-10%',
                    width: '300px',
                    height: '300px',
                    background: 'radial-gradient(circle, rgba(2, 136, 209, 0.1) 0%, transparent 70%)',
                    borderRadius: '50%',
                    zIndex: 0,
                }}
            />
            <Box
                component={motion.div}
                whileHover={{ scale: 1.1 }}
                variants={fadeInUp}
                sx={{
                    width: '100%',
                    maxWidth: 220,
                    height: 220,
                    mb: 4,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    zIndex: 1,
                }}
            >
                <img
                    src="/images/accesD.png"
                    alt="Access Denied"
                    style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'contain',
                        filter: 'drop-shadow(0 6px 14px rgba(0, 0, 0, 0.25))',
                    }}
                />
            </Box>

            <Typography
                variant="h3"
                component={motion.h3}
                variants={fadeInUp}
                sx={{
                    color: '#0288d1',
                    fontWeight: 800,
                    mb: 2,
                    textTransform: 'uppercase',
                    letterSpacing: '1.5px',
                    zIndex: 1,
                }}
            >
                Access Denied
            </Typography>

            <Typography
                variant="body1"
                component={motion.p}
                variants={fadeInUp}
                sx={{
                    color: 'text.secondary',
                    maxWidth: 600,
                    fontWeight: 400,
                    mb: 3,
                    px: { xs: 2, md: 4 },
                    lineHeight: 1.8,
                    fontSize: '1.1rem',
                    zIndex: 1,
                }}
            >
                Sorry, you don’t have permission to view this page. Please contact your administrator for assistance.
            </Typography>

            <Typography
                variant="body2"
                component={motion.p}
                variants={fadeInUp}
                sx={{
                    color: '#d32f2f',
                    fontWeight: 600,
                    mb: 5,
                    fontStyle: 'italic',
                    fontSize: '1rem',
                    zIndex: 1,
                }}
            >
                Restricted Access
            </Typography>

            <Button
                variant="outlined"
                component={motion.button}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.98 }}
                sx={{
                    zIndex: 1,
                    color: '#0288d1',
                    borderColor: '#0288d1',
                    fontWeight: 600,
                    px: 4,
                    py: 1.5,
                    fontSize: '0.95rem',
                    borderRadius: '8px',
                    textTransform: 'none',
                }}
                onClick={() => window.history.back()}
            >
                Go Back
            </Button>
        </Box>
    );
};

export default NoPermissionMessage;
